/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `UserProfileDropdown` for the Caintra Connect frontend.
 * @module components/SideMenu/UserProfileDropdown
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { KeyboardArrowDown } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import styles from "../../styles/SideMenu.module.css";
import { ReactComponent as ProfileIcon } from "../../assets/icons/SideMenu/ProfileIcon.svg";
import { ReactComponent as UsersIcon } from "../../assets/icons/SideMenu/UsersIcon.svg";
import { ReactComponent as MyRequirementsIcon } from "../../assets/icons/SideMenu/MyRequirementsIcon.svg";
import { ReactComponent as RolesIcon } from "../../assets/icons/SideMenu/RolesIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/SideMenu/CheckCircleIcon.svg";
import { ReactComponent as CompaniesIcon } from "../../assets/icons/SideMenu/CompaniesIcon.svg";
import { CONTEXTS, ROUTES } from "./constants";
import AssetImage from "../AssetImage";
import {
  canAccessRequirements,
  canAccessRoles,
  canAccessUsers,
} from "../../utils/accessControl";

/** Renders avatar image, initial letter, or empty placeholder. */
const renderAvatarContent = (user, avatarImageClass, t) => {
  if (user?.imagenDePerfil) {
    return (
      <AssetImage
        src={user.imagenDePerfil}
        alt={t("common.avatarAlt")}
        className={avatarImageClass}
      />
    );
  }
  if (user?.nombre) {
    return user.nombre.charAt(0);
  }
  return "";
};

/**
 * User profile card and expandable dropdown with profile-related navigation links.
 */
const UserProfileDropdown = ({
  user,
  permissions,
  isCollapsed,
  showUserDropdown,
  toggleUserDropdown,
  isMenuItemActive,
  closeDropdownAndNavigate,
  handleMyCompanyClick,
}) => {
  const { t } = useTranslation();
  const avatarContent = renderAvatarContent(user, styles.avatarImage, t);

  const dropdownItemClass = (path, context) =>
    `${styles.userDropdownItem} ${
      isMenuItemActive(path, context) ? styles.active : ""
    }`;

  return (
    <div className={styles.userSection}>
      <div className={styles.userProfileDropdown}>
        <div className={styles.userInfoContainer}>
          <div className={styles.userAvatar}>
            {avatarContent}
            {isCollapsed ? (
              <CheckCircleIcon className={styles.checkmarkIcon} />
            ) : null}
          </div>
          <div className={styles.userDetails}>
            <div className={styles.userName}>
              {user?.nombre || t("common.defaultUserName")}
            </div>
            <div className={styles.onlineStatus}>
              {!isCollapsed ? (
                <CheckCircleIcon className={styles.checkmarkIcon} />
              ) : null}
              <span>{t("pages.menu.online")}</span>
            </div>
          </div>
          <div className={styles.userDropdownContainer}>
            <button
              className={styles.userDropdownButton}
              onClick={toggleUserDropdown}
              aria-expanded={showUserDropdown}
              aria-haspopup="true"
            >
              <KeyboardArrowDown
                className={styles.arrowIcon}
                sx={{
                  transform: showUserDropdown
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s ease-in-out",
                }}
              />
            </button>
          </div>
        </div>

        {showUserDropdown && (
          <div className={styles.userDropdownMenu}>
            <button
              className={styles.userDropdownItem}
              onClick={() =>
                closeDropdownAndNavigate(ROUTES.USERS, {
                  state: { profileUser: user },
                })
              }
            >
              <ProfileIcon className={styles.userDropdownIcon} />
              <span>{t("pages.menu.profile")}</span>
            </button>

            <button
              className={dropdownItemClass(
                ROUTES.COMPANY_PROFILE,
                CONTEXTS.MY_COMPANY,
              )}
              onClick={handleMyCompanyClick}
            >
              <CompaniesIcon className={styles.userDropdownIcon} />
              <span>{t("pages.menu.myCompany")}</span>
            </button>

            {canAccessUsers(user, permissions) && (
              <button
                className={dropdownItemClass(ROUTES.USERS)}
                onClick={() => closeDropdownAndNavigate(ROUTES.USERS)}
              >
                <UsersIcon className={styles.userDropdownIcon} />
                <span>{t("pages.menu.users")}</span>
              </button>
            )}

            {canAccessRequirements(user, permissions) && (
              <button
                className={dropdownItemClass(ROUTES.REQUIREMENTS)}
                onClick={() => closeDropdownAndNavigate(ROUTES.REQUIREMENTS)}
              >
                <MyRequirementsIcon className={styles.userDropdownIcon} />
                <span>{t("pages.menu.myRequirements")}</span>
              </button>
            )}

            {canAccessRoles(user, permissions) && (
              <button
                className={dropdownItemClass(ROUTES.USER_ROLES)}
                onClick={() => closeDropdownAndNavigate(ROUTES.USER_ROLES)}
              >
                <RolesIcon className={styles.userDropdownIcon} />
                <span>{t("pages.menu.roles")}</span>
              </button>
            )}
          </div>
        )}
      </div>
      <div className={styles.divider} />
    </div>
  );
};

export default UserProfileDropdown;
